import axios from 'axios'
import { useAuthStore } from '../../stores/authStore'

let isRefreshing = false
let isRedirectingToLogin = false
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (error?: any) => void }> = []

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve(token)
        }
    })
    failedQueue = []
}

const redirectToLogin = () => {
    if (isRedirectingToLogin) {
        return
    }
    isRedirectingToLogin = true
    processQueue(new Error('Authentication failed'), null)
    useAuthStore.getState().logout()
    window.location.replace('/login') // Simple redirect
}

// Get API base URL from runtime config (injected by server.js)
// In production: https://api.dashbal.com
// In development: http://localhost:3001
const getApiBaseUrl = (): string => {
    // 1. Runtime config injected by server.js (Production & Docker)
    if (typeof window !== 'undefined' && (window as any).__DASHBAL_CONFIG__?.apiBaseUrl) {
        return (window as any).__DASHBAL_CONFIG__.apiBaseUrl
    }

    // 2. Build-time environment variable (esbuild define)
    if (process.env.API_BASE_URL) {
        return process.env.API_BASE_URL
    }

    // 3. Development Fallback
    if (process.env.NODE_ENV !== 'production') {
        return 'http://localhost:3001'
    }

    // 4. Production Fallback
    console.warn('API URL not configured. Defaulting to current origin.')
    return window.location.origin
}

export const apiClient = axios.create({
    baseURL: getApiBaseUrl(),
    timeout: 10000,
    withCredentials: true, // Required for maintenance mode cookies
    headers: {
        'Content-Type': 'application/json',
    },
})

apiClient.interceptors.request.use(
    (config) => {
        if (isRedirectingToLogin) {
            return Promise.reject(new Error('Redirecting to login'))
        }

        // Check if store is initialized
        try {
            const { tokens } = useAuthStore.getState()
            if (tokens?.accessToken) {
                config.headers.Authorization = `Bearer ${tokens.accessToken}`
            }
        } catch (e) {
            // Store might not be ready
        }
        return config
    },
    (error) => Promise.reject(error)
)

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (isRedirectingToLogin) return Promise.reject(new Error('Redirecting to login'))

        const originalRequest = error.config

        if (originalRequest._retry ||
            originalRequest.url?.includes('/admin/auth/login') ||
            originalRequest.url?.includes('/admin/auth/register')) {
            return Promise.reject(error)
        }

        if (error.response?.status === 401) {
            originalRequest._retry = true

            if (originalRequest.url?.includes('/admin/auth/refresh')) {
                redirectToLogin()
                return Promise.reject(error)
            }

            const { tokens } = useAuthStore.getState()
            if (tokens?.refreshToken) {
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject })
                    }).then(() => apiClient(originalRequest))
                }

                isRefreshing = true

                try {
                    // Refresh request
                    const refreshClient = axios.create({
                        baseURL: getApiBaseUrl(),
                        withCredentials: true
                    })
                    const response = await refreshClient.post('/admin/auth/refresh', {
                        refreshToken: tokens.refreshToken
                    })

                    if (response.data.success) {
                        const newTokens = response.data.data.tokens
                        useAuthStore.getState().setTokens(newTokens)
                        originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`
                        processQueue(null, newTokens.accessToken)
                        return apiClient(originalRequest)
                    } else {
                        redirectToLogin()
                        return Promise.reject(new Error('Refresh failed'))
                    }
                } catch (refreshError) {
                    redirectToLogin()
                    return Promise.reject(refreshError)
                } finally {
                    isRefreshing = false
                }
            } else {
                redirectToLogin()
            }
        }
        return Promise.reject(error)
    }
)
